'use client';

import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { usePagesContext } from '@osmosis/nextjs-components/context';
import { useDashboardPrompts } from '../../context/DashboardPromptsContext';
import { DASHBOARD_PROMPTS } from '../../lib/util/dashboardPrompts';
import { DashboardPromptId } from '../../app/_lib/backend/aiContext/ids';
import { track } from '../../lib/util/tracking';
import { buildOsmosisAIUrl } from '../../lib/util/oai';

type Props = {
  disabled?: boolean;
};

/**
 * Dashboard example prompt chips.
 */
const DashboardSearchPromptChips = ({ disabled }: Props) => {
  const { formatMessage } = useIntl();
  const { promptIds, watchedVideoTopic } = useDashboardPrompts();
  const { push, pathname } = usePagesContext();
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!promptIds.length || disabled) {
    return (
      <Flex
        data-testid="dashboard-search-prompt-chips"
        className="min-h-12 w-full"
      />
    );
  }

  const handleChipClick = (id: DashboardPromptId) => {
    const chip = DASHBOARD_PROMPTS[id];
    setIsSubmitting(true);
    track(
      'osmosis-ai-dashboard-prompt-click',
      { page: pathname, prompt: chip.label.defaultMessage },
      ['keen', 'pendo'],
    );

    const prompt = formatMessage(chip.prompt, {
      topic: watchedVideoTopic ?? 'this topic',
    });

    push(
      buildOsmosisAIUrl({
        query: prompt,
        dashboard: id,
        mode: chip.mode,
      }),
    );
  };

  return (
    <Flex
      data-testid="dashboard-search-prompt-chips"
      className="min-h-12 w-full flex-wrap items-center gap-2"
    >
      {promptIds.map((id) => {
        const chip = DASHBOARD_PROMPTS[id];
        return (
          <Button
            key={id}
            disabled={isSubmitting}
            variant="outline"
            rounded
            size="s"
            startIcon={<Icon icon={chip.icon} size={16} />}
            onClick={() => handleChipClick(id)}
            data-testid={`dashboard-prompt-chip-${id}`}
            className="border border-black leading-none hover:border-blue-600 hover:bg-blue-100 hover:text-blue-600"
          >
            {formatMessage(chip.label)}
          </Button>
        );
      })}
    </Flex>
  );
};

export default DashboardSearchPromptChips;
